import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import prestacaoContaService, { type PrestacaoPc } from '../services/prestacaoContaService';
import { type PrestacaoRelatoriosCabecalho } from '../services/relatorioPrestacaoContasService';
import PrestacaoContasAnexosPanel from './prestacaoContasRelatorios/PrestacaoContasAnexosPanel';
import {
  accentTextClass, bodyTextClass, formatCurrency, isSaas,
  secondaryButtonClass, strongTextClass,
} from './prestacaoContasRelatorios/shared';

const PrestacaoContasRelatoriosPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const planoId = searchParams.get('planoId') || '';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pcs, setPcs] = useState<PrestacaoPc[]>([]);
  const [cabecalho, setCabecalho] = useState<PrestacaoRelatoriosCabecalho | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = String((user as any)?.role || (user as any)?.perfil || '').toUpperCase();
  const isAdmin = role === 'ADMIN';
  const podeEditar = ['ADMIN', 'CONVENIOS', 'OPERADOR'].includes(role) || Boolean((user as any)?.gestorParceria);

  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';

  const fetchResumo = async () => {
    if (!planoId) return;
    try {
      setLoading(true);
      setError(null);
      const resumo = await prestacaoContaService.getResumo(planoId);
      setPcs(resumo.pcs);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar a prestação de contas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoId]);

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>Prestação de contas</p>
            <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Relatórios / Anexos</h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
              {cabecalho ? `${cabecalho.planoTitulo} — ${formatCurrency(cabecalho.valorPlano)}` : 'Carregando parceria...'}
            </p>
          </div>
          <button type="button" onClick={() => navigate(-1)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${secondaryButtonClass}`}>
            Voltar
          </button>
        </div>
      </section>

      {!planoId ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          Parâmetro `planoId` ausente na URL.
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      ) : loading && pcs.length === 0 ? (
        <div className={`rounded-2xl border p-4 text-sm ${bodyTextClass}`}>Carregando...</div>
      ) : (
        <PrestacaoContasAnexosPanel
          planoId={planoId}
          pcs={pcs}
          podeEditar={podeEditar}
          onCabecalhoCarregado={setCabecalho}
          onAlterado={fetchResumo}
        />
      )}
    </div>
  );
};

export default PrestacaoContasRelatoriosPage;
